import { z } from 'zod';

export type GhostChat = {
  id: string;
  title: string;
  prompt: string;
  revealAfterHours: number;
};

export type GhostChatReveal = {
  ghost: GhostChat;
  revealAtMs: number;
};

const MS_PER_HOUR = 60 * 60 * 1000;

const GhostChatItemSchema = z
  .object({
    id: z.string().min(1),
    title: z.string().min(1),
    prompt: z.string().min(1),
    reveal_after_hours: z.number().nonnegative().default(0),
  })
  .transform((item): GhostChat => ({
    id: item.id,
    title: item.title,
    prompt: item.prompt,
    revealAfterHours: item.reveal_after_hours,
  }))
  .nullable()
  .catch(null);

const MAX_GHOST_CHATS = 5;

const GhostChatsPayloadSchema = z.object({
  items: z.array(GhostChatItemSchema).default([]),
});

export function parseGhostChatsPayload(payload: unknown): GhostChat[] {
  const parsed = GhostChatsPayloadSchema.safeParse(payload);
  if (!parsed.success) return [];
  const seenIds = new Set<string>();
  return parsed.data.items
    .filter((item): item is GhostChat => item !== null)
    .filter((ghost) => {
      if (seenIds.has(ghost.id)) return false;
      seenIds.add(ghost.id);
      return true;
    })
    .slice(0, MAX_GHOST_CHATS);
}

export function ghostRevealSchedule({
  ghosts,
  accountCreatedAtMs,
  clickedIds,
}: {
  ghosts: GhostChat[];
  accountCreatedAtMs: number | null;
  clickedIds: string[];
}): GhostChatReveal[] {
  const unclickedGhosts = ghosts.filter(
    (ghost) => !clickedIds.includes(ghost.id)
  );

  if (accountCreatedAtMs === null) {
    return unclickedGhosts
      .filter((ghost) => ghost.revealAfterHours === 0)
      .map((ghost) => ({ ghost, revealAtMs: 0 }));
  }

  return unclickedGhosts
    .map((ghost) => ({
      ghost,
      revealAtMs: accountCreatedAtMs + ghost.revealAfterHours * MS_PER_HOUR,
    }))
    .sort((a, b) => a.revealAtMs - b.revealAtMs);
}
